import { Icon, MetaGrid, MetaItem, Panel, RuleId, Screen, ScreenHeader, StatTile, StatusBadge, SummaryTiles, Workspace, WorkspacePane, WatchBadge } from '@garnet/ui';
import { KIND_TAG, type StudioContext } from '../studio.js';
import { StateBlock } from '../components.js';

export function Sources({
  ctx,
  selectedSource,
  setSelectedSource,
}: {
  ctx: StudioContext;
  selectedSource: string | null;
  setSelectedSource: (id: string) => void;
}) {
  const { api } = ctx;
  const sources = api.sources();
  const sel = selectedSource || sources[0]?.id || '';
  const s = api.source(sel);
  const changed = sources.filter((x) => x.watch === 'changed').length;
  const monitoring = sources.filter((x) => x.watch === 'monitoring').length;
  if (!s) return null;

  return (
    <Screen workspace>
      <ScreenHeader
        eyebrow="§ Source of law"
        title="Register"
        lead="The statutes, regulations and standards the rules encode. From a law, reach every dependent rule."
        actions={
          <SummaryTiles min={120} style={{ minWidth: 360 }}>
            <StatTile label="Sources" value={sources.length} />
            <StatTile label="Changed" value={changed} valueStyle={{ color: 'var(--status-warn-text)' }} />
            <StatTile label="Monitoring" value={monitoring} valueStyle={{ color: 'var(--garnet)' }} />
          </SummaryTiles>
        }
      />

      <Workspace style={{ gridTemplateColumns: '1fr 380px' }}>
        <WorkspacePane>
          <div
            className="pane-sticky"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px var(--pad-x)' }}
          >
            <span className="panel-title">Register</span>
            <span className="metatext mono">{sources.length} entries</span>
          </div>
          <table className="tbl">
            <thead>
              <tr>
                <th>Citation</th>
                <th>Kind</th>
                <th>Juris.</th>
                <th>Effective</th>
                <th>Deps</th>
                <th style={{ textAlign: 'right' }}>Watch</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((src) => (
                <tr key={src.id} className={src.id === sel ? 'sel' : ''} onClick={() => setSelectedSource(src.id)}>
                  <td>
                    <span className="mono" style={{ fontSize: 11.5, color: src.id === sel ? 'var(--garnet-text)' : 'var(--fg-1)' }}>
                      {src.citation}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${KIND_TAG[src.kind] || 'b-neutral'}`} style={{ padding: '1px 6px' }}>
                      {src.kind}
                    </span>
                  </td>
                  <td className="mono" style={{ color: 'var(--garnet-text)', fontSize: 11 }}>
                    {src.juris}
                  </td>
                  <td className="mono metatext">{src.effectiveFrom}</td>
                  <td className="mono" style={{ color: src.deps.length ? 'var(--fg-1)' : 'var(--fg-3)' }}>
                    {src.deps.length}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <WatchBadge state={src.watch} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </WorkspacePane>

        <WorkspacePane>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)', padding: 'var(--pad-x)' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
                <span className="panel-title">Source</span>
                <WatchBadge state={s.watch} />
              </div>
              <div className="mono" style={{ fontSize: 14, color: 'var(--garnet-text)', lineHeight: 1.4, marginBottom: 4 }}>
                {s.citation}
              </div>
              <div className="serif" style={{ fontSize: 18, marginBottom: 10 }}>
                {s.title}
              </div>
              <MetaGrid labelWidth={118}>
                <MetaItem label="Kind">
                  <span className={`badge ${KIND_TAG[s.kind] || 'b-neutral'}`} style={{ padding: '1px 6px' }}>
                    {s.kind}
                  </span>
                </MetaItem>
                <MetaItem label="Jurisdiction">
                  <span className="mono" style={{ color: 'var(--garnet-text)' }}>
                    {s.juris}
                  </span>
                </MetaItem>
                <MetaItem label="Effective from">
                  <span className="mono">{s.effectiveFrom}</span>
                </MetaItem>
                <MetaItem label="Last checked">
                  <span className="mono">{s.lastChecked}</span>
                </MetaItem>
                <MetaItem label="Official source">
                  <span
                    className="mono"
                    style={{ fontSize: 11, color: 'var(--status-info-text)', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                  >
                    <Icon name="link" size={11} />
                    {s.url}
                  </span>
                </MetaItem>
              </MetaGrid>
              {s.change && (
                <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--status-warn-bg)', border: '1px solid var(--status-warn)', borderRadius: 4 }}>
                  <div className="label" style={{ color: 'var(--status-warn-text)', marginBottom: 5, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Icon name="alert" size={11} color="var(--status-warn-text)" />
                    Watch · change detected
                  </div>
                  <div className="metatext" style={{ color: 'var(--fg-2)' }}>
                    {s.change}
                  </div>
                </div>
              )}
            </div>

            <Panel flush title="Dependent rules" right={<span className="metatext mono">{s.deps.length}</span>}>
              {s.deps.length === 0 ? (
                <div style={{ padding: 16 }}>
                  <StateBlock kind="empty" title="no dependents" body="No rule cites this source yet." />
                </div>
              ) : (
                <div>
                  {s.deps.map((dep) => {
                    const r = api.rule(dep);
                    return (
                      <div
                        key={dep}
                        onClick={() => ctx.openRule(dep)}
                        role="button"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '9px var(--pad-x)',
                          borderBottom: '1px solid var(--border-subtle)',
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <RuleId id={dep} size={11} {...(r ? { version: r.version } : {})} />
                          {r && (
                            <div className="metatext" style={{ marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 280 }}>
                              {r.title}
                            </div>
                          )}
                        </div>
                        {r ? <StatusBadge status={r.status} /> : <span className="metatext mono">external</span>}
                      </div>
                    );
                  })}
                  {s.watch === 'changed' && (
                    <div className="metatext" style={{ padding: '10px var(--pad-x)', display: 'flex', gap: 6, alignItems: 'center', color: 'var(--status-warn-text)' }}>
                      <Icon name="alert" size={12} color="var(--status-warn-text)" />
                      These rules are flagged into the revisit queue.
                    </div>
                  )}
                </div>
              )}
            </Panel>
          </div>
        </WorkspacePane>
      </Workspace>
    </Screen>
  );
}
